// Activities jobs: build the request body, run it through dataJobs.ts (submit, poll, read) and turn
// the raw rows into ActivityRow. All network calls go through http.ts; nothing is logged.
import type { CountryCode } from '../config/countries';
import type { ActivityMeasure } from '../config/products';
import { LONG_JOB_TIMING, mapOutcomeRows, runDataJob } from './dataJobs';
import type { JobOutcome, RunJobOptions } from './dataJobs';
import { nullableNumber, text, unwrap } from './rowValues';
import { isDemoRouted, runDemoActivitiesJob } from './demoApi';
import { bodiesPerCellSize, rowAreaKey } from './catalogueRequests';
import { exportAllRows } from './fullResults';
import { draftActivitiesJob, isDraftMocked } from './draftMocks';

export interface ActivitiesJobParams {
  agg_type: 'activities' | 'trips';
  measure: ActivityMeasure;
  start_time: string; // UTC timestamp in ISO format
  end_time: string; // UTC timestamp in ISO format
  activity_type: 'daily' | 'hourly';
  geo_type: string; // e.g., "admin_lvl_2", or "model_grid" for a grid area (sent per cell size)
  geo_ids: Array<number | string>; // in the type the area type's registry entry names; grid cells as size-tagged keys
  country_code?: CountryCode;
  /** True asks for every row as a download link instead of a 100-row preview. */
  full: boolean;
}

/** One preview row: the value is the column the request's measure names. */
export interface ActivityRow {
  geo_id: string;
  geo_name: string | null;
  /** The bucket as the API sent it (an hour, or a delivery date on daily rows). */
  time_bucket: string;
  type: string | null;
  /** null when the row has no figure (not published), never 0. */
  value: number | null;
}

const optionalText = (raw: unknown): string | null => (unwrap(raw) == null ? null : text(raw));

/**
 * Normalise one preview row, reading the figure from the column named by `measure`. A row with no
 * area id or no time bucket cannot be placed anywhere, so it is dropped (null). The backend marks
 * an unnamed area as "no_name"; that becomes null so a reader falls back to the area id.
 */
export function normalizeActivityRow(raw: unknown, measure: ActivityMeasure): ActivityRow | null {
  if (raw === null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const rawId = unwrap(r.geo_id);
  // A grid row's id is keyed with its size, as a grid area saves its cells.
  const geoId = rawId == null ? '' : rowAreaKey(rawId, unwrap(r.geo_type));
  const timeBucket = text(r.time_bucket);
  if (geoId === '' || timeBucket === '') return null;
  const name = optionalText(r.geo_name);
  return {
    geo_id: geoId,
    geo_name: name === 'no_name' ? null : name,
    time_bucket: timeBucket,
    type: optionalText(r.type),
    value: nullableNumber(r[measure]),
  };
}

/** Submit an Activities job and wait for its rows. */
export async function runActivitiesJob(params: ActivitiesJobParams, o: RunJobOptions = {}): Promise<JobOutcome<ActivityRow>> {
  if (!params.full && isDraftMocked(params.country_code, 'activities', params.geo_type)) return draftActivitiesJob(params);
  if (isDemoRouted(params.country_code) && !params.full) return runDemoActivitiesJob(params);
  const outcomes = [];
  // A grid area is asked for once per cell size; the first job is the one the page follows, the rest add rows.
  for (const [i, body] of activityRequestBodies(params).entries()) {
    outcomes.push(await runDataJob<unknown>(body, { timing: LONG_JOB_TIMING, ...o, ...(i > 0 ? { onSubmitted: undefined } : {}) }));
  }
  const rows = outcomes.flatMap((outcome) => mapOutcomeRows(outcome, (row) => normalizeActivityRow(row, params.measure)).rows);
  return { kind: 'rows', jobId: outcomes[0].jobId, rows, ...(outcomes.some((outcome) => outcome.truncated) ? { truncated: true as const } : {}) };
}

/** The /data-agg bodies of an Activities request: one, or one per cell size for a model-grid area. */
export function activityRequestBodies(params: ActivitiesJobParams): ActivitiesJobParams[] {
  return bodiesPerCellSize(params);
}

/**
 * Export all rows of an Activities job: the same areas, window and resolution as a full export, one table with one
 * header. It names no measure, so the file has every column, not only the one the preview shows.
 */
export function exportActivities(params: ActivitiesJobParams, o: RunJobOptions = {}): Promise<string[][]> {
  const parts = activityRequestBodies({ ...params, full: true }).map(({ measure, ...body }) => ({ body }));
  return exportAllRows(parts, o);
}
